// Frontend mirror of the backend AUD-2 decision (scripts/lib/assignmentDecision.js),
// used only to render the Audit grid's Status chip and derived Tracking Season.
// The server verb remains the source of truth; this is display-only.
//
// Returns one of:
//   UNASSIGNED - no bucket selected yet
//   UNMAPPED   - selected bucket has no maps_to_trackingseason (not assignable)
//   WILL_SET   - season has no audited trackingSeason -> assigning will derive it
//   AGREES     - audited trackingSeason equals the bucket's mapping
//   MISMATCH   - audited trackingSeason disagrees with the bucket's mapping
//
// Note: trackingSeason arrives from the grid as a string. "" (or null/undefined)
// means unplaced -> WILL_SET. "0" ("Other") is a real value -> VALIDATE (Mismatch
// against a 1-4 bucket), matching the backend's NULL-only DERIVE rule.
export function computeAssignmentStatus(currentTrackingSeason, bucketMapsToTrackingSeason) {
  if (bucketMapsToTrackingSeason === null || bucketMapsToTrackingSeason === undefined) {
    return { status: 'UNMAPPED', mode: null };
  }
  if (currentTrackingSeason === null || currentTrackingSeason === undefined || currentTrackingSeason === '') {
    return { status: 'WILL_SET', mode: 'DERIVE' };
  }
  if (Number(currentTrackingSeason) === Number(bucketMapsToTrackingSeason)) {
    return { status: 'AGREES', mode: 'VALIDATE' };
  }
  return { status: 'MISMATCH', mode: 'VALIDATE' };
}

// Page-level check (AUD-5): a saved season that CAN take a bucket -- its
// franchise-year has an active Franchise Calendar -- but has none assigned yet.
// Drives the "Awaiting bucket" summary counter and mirrors the per-row
// "Assign bucket" cue. Uses persisted values only (season not yet placed).
// Franchise-years with no Franchise Calendar are intentionally excluded: there
// is nothing to assign, so they must not inflate the to-do count.
export function isAwaitingBucket(row, bucketsByKey) {
  if (row.lksi_rev_and_rollseasonsid === null || row.lksi_rev_and_rollseasonsid === undefined) return false;
  const trackingYear = row.TrackingYear ?? row.trackingYear;
  const entry = (bucketsByKey || {})[`${row.com_code ?? row.comCode}|${trackingYear}`];
  const hasCalendar = (entry?.buckets || []).some((b) => b.is_active);
  if (!hasCalendar) return false;
  const seq = row.season_sequence;
  return seq === null || seq === undefined || seq === '';
}

// Coarse bucket category for the Bucket Status column filter, computed from
// persisted values only. Mirrors isAwaitingBucket for the 'awaiting' case.
//   assigned    - saved and a bucket is set
//   awaiting    - saved, a Franchise Calendar exists, no bucket yet
//   no-calendar - saved, franchise-year has no Franchise Calendar
//   unsaved     - not yet saved, so nothing is assigned yet. (The grid still lets you
//                 PICK a bucket on such a row -- it is submitted with Add/Update -- but
//                 until that save there is no persisted assignment to categorise.)
export function bucketAssignmentCategory(row, bucketsByKey) {
  if (row.lksi_rev_and_rollseasonsid === null || row.lksi_rev_and_rollseasonsid === undefined) return 'unsaved';
  const seq = row.season_sequence;
  if (seq !== null && seq !== undefined && seq !== '') return 'assigned';
  return isAwaitingBucket(row, bucketsByKey) ? 'awaiting' : 'no-calendar';
}

// --- Status work queue (the single definition of "workflow complete") ---------
// The audit workflow for an Operational Season is: review + save the metadata,
// then assign a Franchise Calendar bucket. "Complete" means BOTH are done;
// anything else is "Needs Work". This one predicate is shared by the per-row
// Status chip (AuditRow) and the Status column filter (AuditList) so the two can
// never diverge. See docs/projects/audit-status-completeness-review.md.
//
// `accepted` is the drift-aware saved flag: mergeLists resets it to false when a
// warehouse-authoritative field drifts, so a Dash change reopens a bucketed row.
// `seasonSequence` is the assigned bucket. Both callers pass their current values
// (the filter passes persisted values; the row passes live edit state).
export function isBucketAssigned(seasonSequence) {
  return seasonSequence !== null && seasonSequence !== undefined && seasonSequence !== '';
}

export function isWorkflowComplete(accepted, seasonSequence) {
  return Boolean(accepted) && isBucketAssigned(seasonSequence);
}

export const TRACKING_SEASON_LABELS = { 1: 'Spring', 2: 'Summer', 3: 'Fall', 4: 'Winter', 0: 'Other' };

export function trackingSeasonLabel(ts) {
  if (ts === null || ts === undefined || ts === '') return '—';
  return TRACKING_SEASON_LABELS[Number(ts)] ?? String(ts);
}
